import { useEffect, useMemo, useState } from 'react';
import { LuBot, LuKeyRound, LuLoaderPinwheel, LuSend, LuTerminal, LuX } from 'react-icons/lu';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AiCredential } from '../../entities/ai-credential';
import {
  ADR_CHECK_LABEL,
  CODE_AGENT_DEFAULT_TIER,
  CODE_AGENT_PROVIDER_LABEL,
  CODE_AGENT_TIER_DESCRIPTION,
  CODE_AGENT_TIER_LABEL,
  CODE_AGENT_TIER_ORDER,
  CODE_AGENT_TIER_RESOURCES,
  CodeAgentCostEstimate,
  CodeAgentProvider,
  CodeAgentTask,
  CodeAgentTaskStatus,
  CodeAgentTier,
  CodeAgentUsage,
  isAutomatedAdrCheck,
  PR_REVIEW_STATUS_BADGE,
  PR_REVIEW_STATUS_LABEL,
} from '../../entities/code-agent-task';
import { GithubBranch, GithubRepo } from '../../entities/github-repo';
import { listAiCredentials } from '../../shared/api/ai-credentials';
import {
  codeAgentErrorMessage,
  createCodeAgentTask,
  estimateCodeAgentCost,
  getCodeAgentUsage,
  listCodeAgentTasks,
} from '../../shared/api/code-agent';
import { fetchIntegrations, listGithubBranches, listGithubRepos } from '../../shared/api/integration';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';

const REPO_FORMAT = /^([\w.-]+)\/([\w.-]+)$/;

const HOSTED = 'hosted' as const;
type ProviderChoice = typeof HOSTED | CodeAgentProvider;

// Passed via `navigate('/coding-agent', { state: {...} })` from a Standalone task's "Assign to
// agent" action (StandaloneTaskDetailPage.tsx) — linking a run gives the agent extra read-only
// context tools and posts a completion/error comment back onto the task automatically (see the
// "FE: Coding agent — GitHub status fix, task↔project linking, agent comments" knowledge base doc).
interface AssignFromTaskState {
  standaloneTaskId?: string;
  taskKey?: string;
  prompt?: string;
}

const STATUS_BADGE: Record<CodeAgentTaskStatus, string> = {
  queued: 'badge-ghost',
  running: 'badge-info',
  completed: 'badge-success',
  failed: 'badge-error',
  cancelled: 'badge-warning',
};

// Phase 1 (MVPB-66): prompt + task list. Phase 2 (MVPB-68): repo field, gated on GitHub being
// connected since a repo the agent can clone requires that. Phase 3 (MVPB-70): usage/limit
// indicator. Phase 4 (MVPB-72): provider selector — only providers with a saved credential
// (managed on the Integrations page as their own tiles, see
// widgets/integrations/AiProviderIntegration.tsx) are selectable, since BYOK requires a stored key
// on the BE side; picking one bypasses the hosted usage cap entirely.
// MVPB-100: repo picker + branch picker, replacing the earlier plain "owner/repo" text-only field
// (that gap — no repo-listing endpoint — is now closed) and the BE's earlier develop/dev-guessing
// (dropped entirely, per the knowledge base doc, in favor of explicit selection). The repo field
// stays a text input so typing a raw "owner/repo" still works exactly as before; a `<select>`
// next to it just fills that same input from the user's actual repo list. Branches are fetched
// reactively off whatever `repoUrl` currently parses to, so both entry paths — picker or typed —
// converge on the same branch-fetch logic.
// MVPB-102: pre-task cost estimate across all 4 providers, debounced off the prompt text — the
// selected provider's badge is highlighted so switching providers doubles as a cost comparison.
// Real per-task cost (`tokenUsage`/`costUsd`) is shown on CodeAgentTaskPage.tsx instead, once a
// task actually finishes.
export default function CodeAgentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const assignState = location.state as AssignFromTaskState | null;
  const { on, off } = useSocket(auth.currentUser?.uid || '');

  const [tasks, setTasks] = useState<CodeAgentTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [prompt, setPrompt] = useState(assignState?.prompt ?? '');
  const [repoUrl, setRepoUrl] = useState('');
  const [branch, setBranch] = useState('');
  const [isGithubConnected, setIsGithubConnected] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [repos, setRepos] = useState<GithubRepo[]>([]);
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [branches, setBranches] = useState<GithubBranch[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const [linkedTask, setLinkedTask] = useState<{ id: string; key?: string } | null>(
    assignState?.standaloneTaskId ? { id: assignState.standaloneTaskId, key: assignState.taskKey } : null,
  );

  const [usage, setUsage] = useState<CodeAgentUsage | null>(null);
  const [credentials, setCredentials] = useState<AiCredential[]>([]);
  const [providerChoice, setProviderChoice] = useState<ProviderChoice>(HOSTED);
  const [tier, setTier] = useState<CodeAgentTier>(CODE_AGENT_DEFAULT_TIER);

  const [estimates, setEstimates] = useState<CodeAgentCostEstimate[]>([]);
  const [isLoadingEstimate, setIsLoadingEstimate] = useState(false);

  const refreshUsage = () => {
    getCodeAgentUsage()
      .then(setUsage)
      .catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listCodeAgentTasks()
      .then((t) => !cancelled && setTasks(t))
      .catch((err) => !cancelled && setError(codeAgentErrorMessage(err) ?? 'Failed to load tasks.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchIntegrations()
      .then((integrations) => !cancelled && setIsGithubConnected(!!integrations?.isGitConnected))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isGithubConnected) return;
    let cancelled = false;
    setIsLoadingRepos(true);
    listGithubRepos()
      .then((r) => !cancelled && setRepos(r))
      .catch(() => {})
      .finally(() => !cancelled && setIsLoadingRepos(false));
    return () => {
      cancelled = true;
    };
  }, [isGithubConnected]);

  // Reactive branch lookup — fires whichever way `repoUrl` got its value (picked from the select,
  // or typed by hand), debounced so typing doesn't fire a request per keystroke. A previously
  // selected branch is reset immediately (not debounced) since it may not exist in the new repo.
  useEffect(() => {
    setBranch('');
    const match = REPO_FORMAT.exec(repoUrl.trim());
    if (!match) {
      setBranches([]);
      setIsLoadingBranches(false);
      return;
    }
    let cancelled = false;
    setIsLoadingBranches(true);
    const timeout = setTimeout(() => {
      listGithubBranches(match[1], match[2])
        .then((b) => !cancelled && setBranches(b))
        .catch(() => !cancelled && setBranches([]))
        .finally(() => !cancelled && setIsLoadingBranches(false));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [repoUrl]);

  // No live push for usage — re-fetch after every task creation (the only mutation that can
  // change it) instead of polling.
  useEffect(() => {
    refreshUsage();
  }, []);

  // Debounced cost estimate — a rough pre-flight cost across all 4 providers, refetched as the
  // prompt changes. Skipped below a minimum length so half-typed prompts don't spam the endpoint.
  useEffect(() => {
    const trimmed = prompt.trim();
    if (trimmed.length < 15) {
      setEstimates([]);
      setIsLoadingEstimate(false);
      return;
    }
    let cancelled = false;
    setIsLoadingEstimate(true);
    const timeout = setTimeout(() => {
      estimateCodeAgentCost(trimmed)
        .then((e) => !cancelled && setEstimates(e))
        .catch(() => !cancelled && setEstimates([]))
        .finally(() => !cancelled && setIsLoadingEstimate(false));
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [prompt]);

  useEffect(() => {
    let cancelled = false;
    listAiCredentials()
      .then((c) => !cancelled && setCredentials(c))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Live status updates for tasks visible in this list — the detail page owns the actual log
  // stream, this just keeps status badges here fresh without a manual refresh.
  useEffect(() => {
    const onProgress = (data: {
      userId: string;
      taskId: string;
      status: CodeAgentTaskStatus;
      queuePosition?: number;
    }) => {
      setTasks((prev) =>
        prev.map((t) => (t._id === data.taskId ? { ...t, status: data.status, queuePosition: data.queuePosition } : t)),
      );
    };
    on('codeAgentTaskProgress', onProgress);
    return () => off('codeAgentTaskProgress', onProgress as (...args: unknown[]) => void);
  }, [on, off]);

  const isHosted = providerChoice === HOSTED;
  const isLimitReached = isHosted && usage?.remaining === 0;
  const tierUsage = usage?.sandboxStarts?.[tier];
  const isTierLimitReached = tierUsage?.remaining === 0;

  // MF-47 — automated ADR drift-check tasks are grouped into their own section rather than mixed
  // into the flat list, so they don't read as unexplained tasks the user didn't start.
  // MF-50 — a task created by resuming another is nested under its original ("resumedFromTaskId")
  // instead of appearing as an unrelated new entry.
  const { automatedTasks, rootTasks, resumesByParent } = useMemo(() => {
    const automated: CodeAgentTask[] = [];
    const regular: CodeAgentTask[] = [];
    for (const t of tasks) (isAutomatedAdrCheck(t) ? automated : regular).push(t);

    const regularIds = new Set(regular.map((t) => t._id));
    const byParent = new Map<string, CodeAgentTask[]>();
    const roots: CodeAgentTask[] = [];
    for (const t of regular) {
      if (t.resumedFromTaskId && regularIds.has(t.resumedFromTaskId)) {
        const siblings = byParent.get(t.resumedFromTaskId) ?? [];
        siblings.push(t);
        byParent.set(t.resumedFromTaskId, siblings);
      } else {
        roots.push(t);
      }
    }
    return { automatedTasks: automated, rootTasks: roots, resumesByParent: byParent };
  }, [tasks]);

  const handleCreate = async () => {
    if (!prompt.trim() || isLimitReached || isTierLimitReached) return;
    setIsCreating(true);
    setError(null);
    try {
      const created = await createCodeAgentTask({
        prompt: prompt.trim(),
        repoUrl: repoUrl.trim() || undefined,
        branch: branch || undefined,
        aiProvider: isHosted ? undefined : providerChoice,
        standaloneTaskId: linkedTask?.id,
        tier,
      });
      setTasks((prev) => [created, ...prev]);
      setPrompt('');
      setRepoUrl('');
      refreshUsage();
      navigate(`/coding-agent/tasks/${created._id}`, { state: { taskKey: linkedTask?.key } });
    } catch (err) {
      setError(codeAgentErrorMessage(err) ?? 'Failed to start the task.');
      refreshUsage();
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <LuTerminal size={20} className="opacity-60" />
            <span className="text-lg font-semibold">Coding agent</span>
          </div>

          {usage && (
            <div className="flex items-center gap-2">
              {usage.limit === null ? (
                <span className="badge badge-sm badge-ghost">Unlimited</span>
              ) : (
                <>
                  <progress
                    className={`progress w-24 ${isLimitReached ? 'progress-error' : 'progress-primary'}`}
                    value={usage.used}
                    max={usage.limit}
                  />
                  <span className="text-xs opacity-60 whitespace-nowrap">
                    {usage.used}/{usage.limit} tasks this month
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <p className="text-sm opacity-50 max-w-2xl">
          Describe what you want built. mFlow scaffolds a fresh project inside an isolated sandbox and works through it
          autonomously — watch its progress in real time once it starts.
        </p>

        {isLimitReached && (
          <div className="alert alert-warning py-2">
            <LuKeyRound size={16} />
            <span className="text-sm">
              You&apos;ve used all {usage?.limit} hosted tasks this month. Add your own API key in{' '}
              <Link to="/integrations" className="link link-primary">
                Integrations
              </Link>{' '}
              to keep going without a monthly limit.
            </span>
          </div>
        )}

        {isTierLimitReached && (
          <div className="alert alert-warning py-2">
            <LuKeyRound size={16} />
            <span className="text-sm">
              You&apos;ve used all {tierUsage?.limit} {CODE_AGENT_TIER_LABEL[tier]} sandbox starts this month. Pick a
              different sandbox size below, or wait until next month.
            </span>
          </div>
        )}

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {linkedTask && (
          <div className="flex items-center gap-2">
            <span className="badge badge-outline badge-sm gap-1">
              <LuBot size={11} /> Linked to {linkedTask.key ?? 'a standalone task'}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs px-1 min-h-0 h-5 opacity-60"
              onClick={() => setLinkedTask(null)}
              title="Unlink"
            >
              <LuX size={12} />
            </button>
          </div>
        )}

        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4 gap-3">
            <textarea
              className="textarea textarea-bordered w-full"
              placeholder="e.g. Build a Next.js landing page for a coffee shop with a menu section and a contact form"
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <label className="form-control w-full sm:w-56">
                <span className="label-text text-xs mb-1 opacity-60">AI provider</span>
                <select
                  className="select select-bordered select-sm w-full"
                  value={providerChoice}
                  onChange={(e) => setProviderChoice(e.target.value as ProviderChoice)}
                >
                  <option value={HOSTED}>mFlow-hosted (Gemini)</option>
                  {credentials.map((c) => (
                    <option key={c.provider} value={c.provider}>
                      {CODE_AGENT_PROVIDER_LABEL[c.provider]} (your key)
                    </option>
                  ))}
                </select>
              </label>
              {credentials.length === 0 && (
                <p className="text-xs opacity-50 self-end pb-1.5">
                  <Link to="/integrations" className="link link-primary">
                    Add your own API key
                  </Link>{' '}
                  to use a different provider or skip the monthly limit.
                </p>
              )}
            </div>

            <div>
              <span className="label-text text-xs mb-1 opacity-60 block">Sandbox size</span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {CODE_AGENT_TIER_ORDER.map((t) => {
                  const isSelected = tier === t;
                  const tUsage = usage?.sandboxStarts?.[t];
                  const tLimitReached = tUsage?.remaining === 0;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTier(t)}
                      className={`card text-left cursor-pointer transition ${
                        isSelected ? 'shadow-glow bg-primary/5' : 'shadow-card hover:-translate-y-0.5'
                      }`}
                    >
                      <div className="card-body p-3 gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">{CODE_AGENT_TIER_LABEL[t]}</span>
                          {t === CODE_AGENT_DEFAULT_TIER && <span className="badge badge-ghost badge-xs">Default</span>}
                        </div>
                        <span className="text-xs opacity-60 font-mono">{CODE_AGENT_TIER_RESOURCES[t]}</span>
                        <span className="text-xs opacity-50">{CODE_AGENT_TIER_DESCRIPTION[t]}</span>
                        {tUsage && tUsage.limit !== null && (
                          <span className={`text-xs ${tLimitReached ? 'text-error' : 'opacity-40'}`}>
                            {tUsage.used}/{tUsage.limit} starts this month
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {estimates.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs opacity-50">Estimated cost:</span>
                {estimates.map((e) => {
                  const isSelected = e.provider === (isHosted ? 'google' : providerChoice);
                  const isRough = e.basis === 'fallback-assumption';
                  return (
                    <span
                      key={e.provider}
                      className={`badge badge-sm gap-1 ${isSelected ? 'badge-primary' : 'badge-ghost'}`}
                      title={
                        isRough
                          ? 'Rough estimate — no completed tasks yet for this provider'
                          : `Based on ${e.sampleSize} past task${e.sampleSize === 1 ? '' : 's'}`
                      }
                    >
                      {CODE_AGENT_PROVIDER_LABEL[e.provider]}: {isRough ? '~' : ''}${e.estimatedCostUsd.toFixed(4)}
                    </span>
                  );
                })}
              </div>
            )}
            {isLoadingEstimate && estimates.length === 0 && <p className="text-xs opacity-40">Estimating cost…</p>}

            {isGithubConnected ? (
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="form-control w-full sm:w-64">
                    <span className="label-text text-xs mb-1 opacity-60">Pick from your repos</span>
                    <select
                      className="select select-bordered select-sm w-full"
                      value={repos.some((r) => r.fullName === repoUrl) ? repoUrl : ''}
                      onChange={(e) => setRepoUrl(e.target.value)}
                      disabled={isLoadingRepos || repos.length === 0}
                    >
                      <option value="">
                        {isLoadingRepos ? 'Loading…' : repos.length === 0 ? 'No repos found' : 'Select a repo'}
                      </option>
                      {repos.map((r) => (
                        <option key={r.fullName} value={r.fullName}>
                          {r.fullName}
                          {r.private ? ' (private)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="form-control w-full sm:w-48">
                    <span className="label-text text-xs mb-1 opacity-60">Branch (optional)</span>
                    <select
                      className="select select-bordered select-sm w-full"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      disabled={branches.length === 0}
                    >
                      <option value="">{isLoadingBranches ? 'Loading…' : 'Default branch'}</option>
                      {branches.map((b) => (
                        <option key={b.name} value={b.name}>
                          {b.name}
                          {b.protected ? ' (protected)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="form-control w-full max-w-xs">
                  <span className="label-text text-xs mb-1 opacity-60">Or enter owner/repo manually</span>
                  <input
                    type="text"
                    className="input input-bordered input-sm w-full"
                    placeholder="owner/repo"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                  />
                </label>
              </div>
            ) : (
              <p className="text-xs opacity-50">
                Want the agent to work on an existing repo instead of scaffolding a new one?{' '}
                <Link to="/integrations" className="link link-primary">
                  Connect GitHub
                </Link>
                .
              </p>
            )}

            <div className="flex justify-end">
              <button
                className="btn btn-primary btn-sm shadow-glow gap-1.5"
                onClick={handleCreate}
                disabled={isCreating || !prompt.trim() || isLimitReached || isTierLimitReached}
              >
                {isCreating ? <LuLoaderPinwheel className="animate-spin" size={14} /> : <LuSend size={14} />}
                Start task
              </button>
            </div>
          </div>
        </div>

        {automatedTasks.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium opacity-50">Automated checks</span>
            <div className="space-y-2">
              {automatedTasks.map((task) => (
                <Link
                  key={task._id}
                  to={`/coding-agent/tasks/${task._id}`}
                  className="card bg-base-100 shadow-card hover:-translate-y-0.5 transition block opacity-70"
                >
                  <div className="card-body p-3 flex-row items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm italic truncate">{ADR_CHECK_LABEL}</p>
                      <p className="text-xs opacity-40 mt-0.5">{new Date(task.createdAt).toLocaleString()}</p>
                    </div>
                    <span className={`badge badge-sm shrink-0 ${STATUS_BADGE[task.status]}`}>{task.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <span className="text-sm font-medium opacity-70">Tasks</span>
          {isLoading ? (
            <div className="flex justify-center py-6">
              <LuLoaderPinwheel className="animate-spin opacity-40" size={20} />
            </div>
          ) : rootTasks.length === 0 ? (
            <p className="text-sm opacity-40 py-4 text-center">No coding agent tasks yet.</p>
          ) : (
            <div className="space-y-2">
              {rootTasks.map((task) => (
                <div key={task._id} className="space-y-2">
                  <Link
                    to={`/coding-agent/tasks/${task._id}`}
                    className="card bg-base-100 shadow-card hover:-translate-y-0.5 transition block"
                  >
                    <div className="card-body p-4 flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{task.prompt}</p>
                        <p className="text-xs opacity-40 mt-0.5">
                          {new Date(task.createdAt).toLocaleString()}
                          {task.repoUrl && ` · ${task.repoUrl}${task.branch ? `@${task.branch}` : ''}`}
                          {task.standaloneTaskId && ' · linked task'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {task.prReviewStatus && (
                          <span className={`badge badge-sm ${PR_REVIEW_STATUS_BADGE[task.prReviewStatus]}`}>
                            {PR_REVIEW_STATUS_LABEL[task.prReviewStatus]}
                          </span>
                        )}
                        {task.status === 'queued' && task.queuePosition != null && (
                          <span
                            className="badge badge-ghost badge-sm"
                            title="Approximate — can shift as other tasks finish or are cancelled"
                          >
                            Queued — position {task.queuePosition}
                          </span>
                        )}
                        <span className={`badge badge-sm ${STATUS_BADGE[task.status]}`}>{task.status}</span>
                      </div>
                    </div>
                  </Link>

                  {(resumesByParent.get(task._id) ?? []).map((resumed) => (
                    <Link
                      key={resumed._id}
                      to={`/coding-agent/tasks/${resumed._id}`}
                      className="card bg-base-100 shadow-card hover:-translate-y-0.5 transition block ml-6 border-l-2 border-base-300"
                    >
                      <div className="card-body p-4 flex-row items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs opacity-50 mb-0.5">↳ Resumed with review feedback</p>
                          <p className="text-sm truncate">{resumed.prompt}</p>
                          <p className="text-xs opacity-40 mt-0.5">{new Date(resumed.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {resumed.prReviewStatus && (
                            <span className={`badge badge-sm ${PR_REVIEW_STATUS_BADGE[resumed.prReviewStatus]}`}>
                              {PR_REVIEW_STATUS_LABEL[resumed.prReviewStatus]}
                            </span>
                          )}
                          <span className={`badge badge-sm ${STATUS_BADGE[resumed.status]}`}>{resumed.status}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
